import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgModule,
  OnChanges,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { SharedModule } from '../../shared/shared.module';
import { CheckboxModule } from '../checkbox/checkbox.component';
import { ChipModule } from '../chip/chip.component';
import { CircleButtonModule } from '../circle-button/circle-button.component';
import { BlueCircleButtonConfig } from '../circle-button/circle-button.props';
import { rotateAnimation, showAnimation } from './multi-select-dropdown-animations';

interface CheckboxOptionType<T> {
  value: T;
  checked: boolean;
}

@Component({
  selector: 'lp2-multi-select-dropdown',
  templateUrl: './multi-select-dropdown.component.html',
  styleUrls: ['./multi-select-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [showAnimation, rotateAnimation],
})
export class MultiSelectDropdownComponent<T> implements OnInit, OnChanges, OnDestroy {
  @Input() placeholder: string;
  @Input() valueKey: string;
  @Input() labelKey: string;
  @Input() selectedOptions: T[] = [];
  @Input() maxSelection: number;
  @Input() modelType: { new(...args: T[]): T };
  @Input() options: T[];
  @Input() comingSoonOptions: T[];
  @Output() selectionChange = new EventEmitter();
  @Output() maxItemsReached = new EventEmitter();
  @Output() toggled: EventEmitter<boolean> = new EventEmitter();

  blueCircleButtonConfig = BlueCircleButtonConfig;
  _opened = new BehaviorSubject(false);
  checkboxOptions: CheckboxOptionType<T>[] = [];

  private wasOpened = false;
  private subscriptions = new Subscription();

  @HostListener('document:click', ['$event']) clickout(event: MouseEvent) {
    if (this.wasOpened) {
      this.wasOpened = false;
      return;
    }
    if (!this.eRef.nativeElement.contains(event.target)) {
      this._opened.next(false);
    }
  }

  constructor(private eRef: ElementRef) { }

  ngOnInit() {
    this.subscriptions.add(
      this._opened.asObservable().subscribe((opened) => {
        if (opened) {
          this.wasOpened = true;
        }
      })
    );
  }

  ngOnChanges() {
    this.checkboxOptions = this.getCheckboxOptions(this.options);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  get opened$() {
    return this._opened.asObservable();
  }

  get showHide$() {
    return this._opened.asObservable().pipe(map((opened) => (opened ? 'show' : 'hide')));
  }

  get noSelectedOptions() {
    return this.selectedOptions.length === 0;
  }

  get maxOptionsSelected() {
    if (this.maxSelection === undefined) {
      return false;
    }
    return this.selectedOptions.length === this.maxSelection;
  }

  open() {
    this.toggle(true);
  }

  toggle(opened?: boolean) {
    this._opened.next(opened ? opened : !this._opened.value);
    this.toggled.emit(this._opened.value);
  }

  onCheckboxOptionChange({ target: { checked } }: { target: { checked: boolean } }, changedOption: T) {
    if (checked) {
      this.addToSelected(changedOption);
    } else {
      this.removeFromSelected(changedOption);
    }
  }

  onCheckboxOptionClick(ev: MouseEvent, checkBoxOption: CheckboxOptionType<T>) {
    if (this.maxSelection === undefined) {
      return;
    }
    if (this.selectedOptions.length === this.maxSelection && !checkBoxOption.checked) {
      ev.preventDefault();
      this.maxItemsReached.emit();
    }
  }

  removeFromSelected(changedOption: T) {
    const newSelectedOptions = this.selectedOptions.filter(
      (option) => option[this.valueKey] !== changedOption[this.valueKey]
    );
    this.selectionChange.emit(newSelectedOptions);
  }

  private addToSelected(changedOption: T) {
    const newSelectedOptions = [...this.selectedOptions, changedOption];
    this.selectionChange.emit(newSelectedOptions);
  }

  private getCheckboxOptions(options: T[]) {
    return options.map((option) => {
      const checked = !!this.selectedOptions.find((current) => current[this.valueKey] === option[this.valueKey]);
      return {
        value: option,
        checked,
      };
    });
  }
}

@NgModule({
  imports: [CommonModule, CircleButtonModule, CheckboxModule, ChipModule, SharedModule],
  declarations: [MultiSelectDropdownComponent],
  exports: [MultiSelectDropdownComponent],
})
export class MultiSelectDropdownModule { }



// WEBPACK FOOTER //
// ./ts/app/components/multi-select-dropdown/multi-select-dropdown.component.ts